// sound.ts — the universal, customizable sound-alert module (v2-plan S7.2). Lives in
// @ozpanel/ui (not the mailbox app) because "universal" was the requirement: any v2 shell
// (mailbox now, admin's own high-priority alerts later, per S5.1's alert_policy) reads the
// SAME module, not a mailbox-specific one.
//
// Synthesizes tones via the Web Audio API rather than shipping audio asset files — no
// external files to ship/license/pick formats for (G46 left the exact file set undecided;
// this sidesteps that by not needing files at all), and it is genuinely audible in a real
// browser, not a silent placeholder.
//
// Unlock-on-first-click (browser autoplay policy): an AudioContext starts "suspended" until
// resumed inside a real user gesture. armUnlock() attaches a persistent (not one-time)
// listener that resumes it on the next click/keydown/touchstart — persistent, not
// `{once:true}`, because a backgrounded tab can suspend the context again and a single past
// gesture does not guarantee every later one; play() itself NEVER throws and NEVER plays
// before a real resume has happened, so a caller can call it unconditionally on every new-
// mail event without checking state first.

export type SoundTheme = "default" | "chime" | "bell" | "none";

function tone(ctx: AudioContext, freq: number, startTime: number, duration: number, gain = 0.28) {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  // A linear attack then exponential decay avoids the harsh click a bare on/off gain produces.
  g.gain.setValueAtTime(0, startTime);
  g.gain.linearRampToValueAtTime(gain, startTime + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start(startTime);
  osc.stop(startTime + duration + 0.05);
}

// Each theme is a short, distinct sequence of notes — deliberately simple, no samples.
const THEMES: Record<Exclude<SoundTheme, "none">, (ctx: AudioContext) => void> = {
  default: (ctx) => {
    tone(ctx, 880.0, ctx.currentTime, 0.15);
    tone(ctx, 1174.66, ctx.currentTime + 0.12, 0.2);
  },
  chime: (ctx) => {
    tone(ctx, 659.25, ctx.currentTime, 0.12);
    tone(ctx, 783.99, ctx.currentTime + 0.1, 0.12);
    tone(ctx, 987.77, ctx.currentTime + 0.2, 0.28);
  },
  bell: (ctx) => {
    tone(ctx, 1046.5, ctx.currentTime, 0.7, 0.22);
    tone(ctx, 2093.0, ctx.currentTime, 0.5, 0.06); // a faint overtone, the way a real bell has one
  },
};

export function isKnownSoundTheme(v: string): v is SoundTheme {
  return v === "default" || v === "chime" || v === "bell" || v === "none";
}

export class SoundModule {
  private ctx: AudioContext | null = null;
  private armed = false;

  /**
   * Call once, as early as the app mounts. Safe to call multiple times (idempotent) — a
   * component remounting must not attach a second set of listeners.
   */
  armUnlock(): void {
    if (this.armed) return;
    this.armed = true;
    const unlock = () => {
      const ctx = this.ensureContext();
      if (ctx.state === "suspended") {
        void ctx.resume().catch(() => {
          // Nothing to do — the next gesture tries again, and play() stays a safe no-op
          // until one succeeds.
        });
      }
    };
    for (const evt of ["click", "keydown", "touchstart"] as const) {
      document.addEventListener(evt, unlock, { passive: true });
    }
  }

  /** Whether a real user gesture has successfully unlocked audio yet. */
  isUnlocked(): boolean {
    return this.ctx !== null && this.ctx.state === "running";
  }

  private ensureContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
    }
    return this.ctx;
  }

  /**
   * play never throws and never plays before armUnlock's gesture has actually unlocked
   * audio — a caller (e.g. a new-mail handler) can call this unconditionally on every event
   * without checking state or wrapping it in a try/catch itself.
   */
  play(theme: string): void {
    if (!isKnownSoundTheme(theme)) {
      return;
    }
    if (theme === "none") {
      return;
    }
    if (!this.isUnlocked()) {
      return;
    }
    const play = THEMES[theme];
    play(this.ensureContext());
  }
}

/** One SoundModule per app — a second instance would attach a second set of unlock listeners
 * and, worse, arm a second AudioContext that autoplay policy would treat independently. */
export const sound = new SoundModule();
